// Adapted from https://21st.dev/aceternity/sidebar/default for React Router

import { LinkProps, NavLink, useLocation } from 'react-router'
import React, { useState, createContext, useContext, useMemo, useCallback } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Menu, X } from 'lucide-react'

import { cn } from '@/lib/utils'

interface Links {
  name: string
  href: string
  icon: React.JSX.Element | React.ReactNode
  external?: boolean
  exact?: boolean
}

interface SidebarContextProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  animate: boolean
}

const SidebarContext = createContext<SidebarContextProps | undefined>(undefined)

export const useSidebar = () => {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}

export const SidebarProvider = ({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true,
}: {
  children: React.ReactNode
  open?: boolean
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>
  animate?: boolean
}) => {
  const [openState, setOpenState] = useState(false)

  const open = openProp !== undefined ? openProp : openState
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState

  const contextValue = useMemo(
    () => ({ open, setOpen, animate }),
    [open, setOpen, animate]
  )

  return (
    <SidebarContext.Provider value={contextValue}>
      {children}
    </SidebarContext.Provider>
  )
}

export const Sidebar = ({
  children,
  open,
  setOpen,
  animate,
}: {
  children: React.ReactNode
  open?: boolean
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>
  animate?: boolean
}) => {
  return (
    <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </SidebarProvider>
  )
}

interface SidebarBodyProps {
  className?: string
  children?: React.ReactNode
}

export const SidebarBody = ({ className, children }: SidebarBodyProps) => {
  return (
    <>
      <DesktopSidebar className={className}>{children}</DesktopSidebar>
      <MobileSidebar>{children}</MobileSidebar>
    </>
  )
}

export const DesktopSidebar = React.memo(({
  className,
  children,
}: SidebarBodyProps) => {
  const { open, setOpen, animate } = useSidebar()
  const width = '240px'

  const handleMouseEnter = useCallback(() => setOpen(true), [setOpen])
  const handleMouseLeave = useCallback(() => setOpen(false), [setOpen])

  const animateWidth = useMemo(() => ({
    width: animate ? (open ? width : '60px') : width,
  }), [animate, open, width])

  return (
    <motion.div
      className={cn(
        'h-full px-2 py-4 hidden md:flex md:flex-col flex-shrink-0',
        `w-[${width}]`,
        className
      )}
      animate={animateWidth}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </motion.div>
  )
})

DesktopSidebar.displayName = 'DesktopSidebar'

export const MobileSidebar = React.memo(({
  className,
  children,
}: {
  className?: string
  children?: React.ReactNode
}) => {
  const { open, setOpen } = useSidebar()

  const toggleMobile = useCallback(() => setOpen(!open), [setOpen, open])

  return (
    <>
      <div
        className={cn(
          'h-10 px-2 py-4 flex flex-row md:hidden items-center justify-between w-full'
        )}
      >
        <div className="flex justify-end z-20 w-full">
          <Menu
            className="text-neutral-800 dark:text-neutral-200 cursor-pointer"
            onClick={toggleMobile}
          />
        </div>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ x: '-100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '-100%', opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: 'easeInOut',
              }}
              className={cn(
                'fixed h-full w-full inset-0 bg-white dark:bg-neutral-900 p-10 z-[100] flex flex-col justify-between',
                className
              )}
            >
              <div
                className="absolute right-10 top-10 z-50 text-neutral-800 dark:text-neutral-200 cursor-pointer"
                onClick={toggleMobile}
              >
                <X />
              </div>
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  )
})

MobileSidebar.displayName = 'MobileSidebar'

export const SidebarLink = React.memo(({
  link,
  className,
  ...props
}: {
  link: Links
  className?: string
} & Omit<LinkProps, 'to'>) => {
  const location = useLocation();
  const { open, animate } = useSidebar()

  const isEnd = useCallback(() => {
    if (location.pathname === link.href) return true;
    if (link.exact) {
      return !location.pathname.split('/').some((part: string) =>part.trim() ? !isNaN(Number(part)): false );
    }
    return false;
  }, [location.pathname, link.href])

  if (link.external) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'flex items-center justify-start gap-2 group/sidebar px-2 py-2 text-gray-800',
          className
        )}
        {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        <div className="flex-shrink-0 [&>svg]:w-4 [&>svg]:h-4">
          {link.icon}
        </div>
        <motion.span
          animate={{
            display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
            opacity: animate ? (open ? 1 : 0) : 1,
          }}
          className="text-detail group-hover/sidebar:translate-x-1 transition duration-150 whitespace-pre inline-block !p-0 !m-0"
        >
          {link.name}
        </motion.span>
      </a>
    )
  }

  return (
    <NavLink
      to={link.href}
      className={({ isActive }) =>
      [
        cn(
          'flex items-center justify-start gap-2 group/sidebar px-2 py-2 text-gray-800',
          className
        ),
        isActive ? "bg-green-100 text-green-700 font-semibold" : "hover:bg-green-50",
      ].join(' ')
      }
      end={isEnd()}
      {...props}
    >
      <div className="flex-shrink-0 [&>svg]:w-4 [&>svg]:h-4">
        {link.icon}
      </div>
      <motion.span
        animate={{
          display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        className="text-detail group-hover/sidebar:translate-x-1 transition duration-150 whitespace-pre inline-block !p-0 !m-0"
      >
        {link.name}
      </motion.span>
    </NavLink>
  )
})

SidebarLink.displayName = 'SidebarLink'